import React from "react";
import {
  Box,
  Typography,
  Button,
  Stack,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

// Assets
import { ReactComponent as CloseIcon } from "assets/images/icons/icn_close.svg";

// Components
import { SolidButton } from "components/buttons/SolidButton";

const ResultView = ({
  viewStateEnum,
  getViewTitle,
  form,
  results,
  closeModal,
}) => {
  const theme = useTheme();

  const totalCredits = results.reduce((sum, item) => {
    return sum + (item.status === "success" ? (item.change || 0) : 0);
  }, 0);

  const validCount = results.filter((item) => item.status === "success").length;
  const invalidCount = results.filter((item) => item.status === "error").length;

  const successColor = "#008514";
  const errorColor = "#E51A2E";
  const darkBlue = "#172664";

  return (
    <Box style={{ position: "relative", padding: "0" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1
        }}
      >
        <Typography
          sx={{
            color: darkBlue,
            fontFamily: theme.typography.fontFamily,
            fontSize: theme.typography.customFontSize[5],
            fontWeight: theme.typography.customFontWeight[3],
            paddingLeft: "20px",
          }}
        >
          {getViewTitle(viewStateEnum.RESULT)}
        </Typography>
        <Button
          onClick={closeModal}
          sx={{
            backgroundColor: theme.palette.primary.transparent,
            boxShadow: "none",
            "&:hover": {
              backgroundColor: theme.palette.primary.transparent,
            },
          }}
        >
          <CloseIcon />
        </Button>
      </Box>

      <Box sx={{ paddingLeft: "26px", paddingRight: "26px" }}>
        <Typography
          sx={{
            fontSize: "14px",
            fontWeight: 600,
            marginTop: "16px",
            marginBottom: "8px",
            color: darkBlue
          }}
        >
          Result
        </Typography>

        <Box sx={{ ...form.getTableStyle() }}>
          <TableContainer
            component={Paper}
            sx={{
              "&.MuiPaper-root": {
                boxShadow: "none",
                border: 'none',
              },
            }}
            style={{ height: '300px', overflowY: 'auto' }}
          >
            <Table
              sx={{
                borderCollapse: "collapse",
                "& td, & th": {
                  border: "none",
                },
                "& tbody tr:nth-of-type(odd)": {
                  backgroundColor: 'rgb(246, 246, 246)',
                },
                "& tbody tr:nth-of-type(even)": {
                  backgroundColor: theme.palette.primary.white,
                },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      color: darkBlue,
                    }}
                  >
                    License Key
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: 600,
                      color: darkBlue,
                    }}
                  >
                    Credits
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {results.map((item, index) => {
                  const segments = (item.licenseKey || "").split("-");
                  return (
                    <TableRow key={index}>
                      <TableCell sx={{ padding: '12px 16px' }}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          {segments.map((segment, segIndex) => (
                            <React.Fragment key={segIndex}>
                              <Box
                                sx={{
                                  width: "70px",
                                  height: "32px",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  border: `1px solid ${theme.palette.primary.borderGreyPrimary}`,
                                  borderRadius: "10px",
                                  backgroundColor: theme.palette.primary.white,
                                  fontSize: "13px",
                                  color: theme.palette.text.primary
                                }}
                              >
                                {segment}
                              </Box>
                              {segIndex < 4 && (
                                <Box
                                  sx={{
                                    color: theme.palette.primary.greyPrimary,
                                  }}
                                >
                                  —
                                </Box>
                              )}
                            </React.Fragment>
                          ))}
                        </Stack>
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          fontWeight: 600,
                          color:
                            item.status === "success"
                              ? successColor
                              : errorColor,
                        }}
                      >
                        {item.status === "success"
                          ? `+${(item.change || 0).toLocaleString()}`
                          : "Invalid"}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        <Typography
          sx={{
            marginTop: "16px",
            fontSize: "14px",
            color: theme.palette.text.primary,
          }}
        >
          Result:{" "}
          <Box component="span" sx={{ fontWeight: 600, color: successColor }}>
            {validCount}
          </Box>{" "}
          valid,{" "}
          <Box component="span" sx={{ fontWeight: 600, color: errorColor }}>
            {invalidCount}
          </Box>{" "}
          invalid. Total credits added up:{" "}
          <Box component="span" sx={{ fontWeight: 600, color: successColor }}>
            +{totalCredits.toLocaleString()}
          </Box>
        </Typography>
      </Box>

      <Box
        sx={{
          margin: "24px 0 16px 0",
          display: "flex",
          justifyContent: "center",
        }}
      >
        <SolidButton
          onClick={closeModal}
          width="120px"
          height="36px"
          sx={{
            borderRadius: "20px",
            bgcolor: darkBlue,
            "&:hover": {
              bgcolor: "#23367d",
            },
          }}
        >
          Close
        </SolidButton>
      </Box>
    </Box>
  );
};

export default ResultView;
